import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfigFotos, useGuardarConfigFotos, useObrasFotos } from '@/hooks/useFotos';
import type { MotivoHabilitacion, ObraFotos, TipoCentroCosto } from '@adelante/shared';
import { CircleNotch, MagnifyingGlass } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

/**
 * Configuración de centros de costo para fotos (admin):
 *   - Días después de la entrega en que una casa aún admite fotos.
 *   - Áreas de costeo de BC → tipo de obra (y si admiten fotos).
 *   - Excepciones por centro de costo (habilitar / deshabilitar a mano).
 * Ver una galería nunca se restringe; esto solo controla subir fotos nuevas.
 */

export const TIPOS_CC: Record<TipoCentroCosto, string> = {
  vivienda: 'Vivienda',
  infraestructura: 'Infraestructura',
  fabrica: 'Fábrica',
  terceros: 'Casas a terceros',
  locales: 'Locales comerciales',
  externos: 'Externos',
  administrativo: 'Administrativo',
  otro: 'Otro',
};

const MOTIVO: Record<MotivoHabilitacion, string> = {
  automatica: 'Habilitada',
  entregada: 'Entregada',
  area: 'Área sin fotos',
  manual_habilitada: 'Habilitada (manual)',
  manual_deshabilitada: 'Deshabilitada (manual)',
};

const SELECT = 'h-8 rounded-md border border-input bg-background px-2 text-xs';

export function CentrosCostoFotosPantalla() {
  const configQ = useConfigFotos();
  const guardar = useGuardarConfigFotos();
  const [dias, setDias] = useState('');

  useEffect(() => {
    if (configQ.data) setDias(String(configQ.data.dias_post_entrega));
  }, [configQ.data]);

  async function guardarDias() {
    const n = Number(dias);
    if (!Number.isInteger(n) || n < 0) {
      toast.error('Ingrese un número de días válido');
      return;
    }
    try {
      await guardar.dias.mutateAsync(n);
      toast.success(`Margen post-entrega: ${n} días`);
    } catch (e) {
      toast.error('No se pudo guardar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Centros de costo (fotos)</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Todos los centros de costo de Business Central. Aquí se clasifican y se decide cuáles
          admiten fotos nuevas. Las galerías siempre se pueden ver.
        </p>
      </div>

      {/* Regla de entrega */}
      <Card>
        <CardHeader>
          <CardTitle>Casas entregadas</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label htmlFor="dias-entrega" className="text-sm font-medium">
              Días después de la entrega en que aún se suben fotos
            </label>
            <Input
              id="dias-entrega"
              type="number"
              min={0}
              value={dias}
              onChange={(e) => setDias(e.target.value)}
              className="w-28"
            />
          </div>
          <Button
            onClick={guardarDias}
            disabled={guardar.dias.isPending || dias === String(configQ.data?.dias_post_entrega)}
          >
            Guardar
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            La fecha de entrega sale del caso de venta. Pasado ese margen la casa deja de aceptar
            fotos, salvo que se habilite a mano abajo.
          </p>
        </CardContent>
      </Card>

      <AreasCard />
      <CentrosCard />
    </div>
  );
}

// ============================================================================
// Áreas de costeo
// ============================================================================

function AreasCard() {
  const configQ = useConfigFotos();
  const guardar = useGuardarConfigFotos();
  const areas = configQ.data?.areas ?? [];

  async function cambiar(area: string, tipo: TipoCentroCosto, admite: boolean) {
    try {
      await guardar.area.mutateAsync({ area_costeo: area, tipo, admite_fotos: admite });
      toast.success(`${area}: ${TIPOS_CC[tipo]}${admite ? '' : ' · sin fotos'}`);
    } catch (e) {
      toast.error('No se pudo guardar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Clasificación por área de costeo</CardTitle>
        <p className="text-sm text-muted-foreground">
          El área de costeo es la Dimensión Global 2 de BC. Un área nueva aparece aquí como "Sin
          clasificar" y cuenta como "Otro" hasta que se le asigne un tipo.
        </p>
      </CardHeader>
      <CardContent>
        {configQ.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Área de costeo</TableHead>
                <TableHead className="text-center">Centros de costo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-center">Admite fotos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas.map((a) => (
                <TableRow key={a.area_costeo}>
                  <TableCell className="font-mono text-xs">
                    {a.area_costeo}
                    {!a.configurada && (
                      <Badge variant="secondary" className="ml-2 text-[10px]">
                        Sin clasificar
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">{a.n_obras}</TableCell>
                  <TableCell>
                    <select
                      className={SELECT}
                      value={a.tipo}
                      onChange={(e) =>
                        cambiar(a.area_costeo, e.target.value as TipoCentroCosto, a.admite_fotos)
                      }
                    >
                      {Object.entries(TIPOS_CC).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={a.admite_fotos}
                      onCheckedChange={(v) => cambiar(a.area_costeo, a.tipo, v)}
                      aria-label={`${a.area_costeo} admite fotos`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Centros de costo (excepciones)
// ============================================================================

function CentrosCard() {
  const obrasQ = useObrasFotos('');
  const guardar = useGuardarConfigFotos();
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState<TipoCentroCosto | ''>('');
  const [estado, setEstado] = useState<'' | 'habilitadas' | 'deshabilitadas' | 'excepciones'>('');

  const lista = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return (obrasQ.data ?? [])
      .filter((o) => !q || `${o.codigo} ${o.nombre ?? ''}`.toLowerCase().includes(q))
      .filter((o) => !tipo || o.tipo === tipo)
      .filter((o) =>
        estado === 'habilitadas'
          ? o.habilitada
          : estado === 'deshabilitadas'
            ? !o.habilitada
            : estado === 'excepciones'
              ? o.fotos_habilitada !== null
              : true,
      )
      .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));
  }, [obrasQ.data, texto, tipo, estado]);

  async function excepcion(o: ObraFotos, valor: string) {
    const fotos_habilitada = valor === '' ? null : valor === 'si';
    try {
      await guardar.obra.mutateAsync({ codigo: o.codigo, fotos_habilitada });
      toast.success(
        `${o.codigo}: ${fotos_habilitada === null ? 'regla automática' : fotos_habilitada ? 'habilitada' : 'deshabilitada'}`,
      );
    } catch (e) {
      toast.error('No se pudo guardar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Centros de costo</CardTitle>
        <p className="text-sm text-muted-foreground">
          "Automático" aplica la regla (área + entrega). Use "Habilitada" o "Deshabilitada" para
          forzar una excepción, por ejemplo para permitir fotos de postventa en una casa entregada.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-[200px] flex-1">
            <MagnifyingGlass
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              weight="bold"
              aria-hidden="true"
            />
            <Input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Buscar código o nombre…"
              className="pl-8"
            />
          </div>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoCentroCosto | '')}
          >
            <option value="">Todos los tipos</option>
            {Object.entries(TIPOS_CC).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={estado}
            onChange={(e) => setEstado(e.target.value as typeof estado)}
          >
            <option value="">Todos</option>
            <option value="habilitadas">Admiten fotos</option>
            <option value="deshabilitadas">No admiten fotos</option>
            <option value="excepciones">Con excepción manual</option>
          </select>
        </div>

        {obrasQ.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{lista.length} centros de costo</p>
            <div className="max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Entrega</TableHead>
                    <TableHead>Fotos</TableHead>
                    <TableHead>Excepción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lista.map((o) => (
                    <TableRow key={o.codigo}>
                      <TableCell className="font-mono text-xs font-semibold">{o.codigo}</TableCell>
                      <TableCell className="max-w-[220px] truncate text-xs">
                        {o.nombre ?? '—'}
                        <span className="block text-[10px] text-muted-foreground">
                          {o.area_costeo}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{TIPOS_CC[o.tipo]}</TableCell>
                      <TableCell className="text-xs tabular-nums">
                        {o.fecha_entrega
                          ? new Date(`${o.fecha_entrega}T12:00:00`).toLocaleDateString('es-CR')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={o.habilitada ? 'default' : 'secondary'}
                          className={
                            o.habilitada
                              ? 'bg-adelante-primary text-adelante-foreground hover:bg-adelante-primary'
                              : ''
                          }
                        >
                          {MOTIVO[o.motivo]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <select
                          className={SELECT}
                          value={
                            o.fotos_habilitada === null ? '' : o.fotos_habilitada ? 'si' : 'no'
                          }
                          onChange={(e) => excepcion(o, e.target.value)}
                        >
                          <option value="">Automático</option>
                          <option value="si">Habilitada</option>
                          <option value="no">Deshabilitada</option>
                        </select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
